import {
    getIndexes,
    getDefaultIndexes,
    getFuturesLastOrderIndexes,
    getFuturesLiquidationIndexes,
    getLastOrderIndexes,
} from '../../services/BeholderService';
import { getMonitorsBySymbol } from '../../services/MonitorsService';

export function filterOrderTemplatesForPair(templates, symbol, { side, market } = {}) {
    if (!symbol) return [];

    const normalized = String(symbol).trim().toUpperCase();
    return (templates || []).filter((order) => {
        if ((order.symbol || '').toUpperCase() !== normalized) return false;

        if (side && order.side && order.side.toUpperCase() !== String(side).toUpperCase()) {
            return false;
        }

        if (market === 'FUTURES' && !order.leverage) return false;
        if (market === 'SPOT' && order.leverage) return false;

        return true;
    });
}

export function filterMonitorsBySymbol(monitors, symbol) {
    if (!symbol) return [];

    const normalized = String(symbol).trim().toUpperCase();
    return (monitors || []).filter(
        (monitor) => monitor.symbol === normalized || monitor.symbol === '*',
    );
}

export function getMonitorDisplayName(monitor) {
    if (!monitor) return 'Monitor';

    const customName = (monitor.name || '').trim();
    if (customName) return customName;

    const broadcastLabel = (monitor.broadcastLabel || '').trim();
    if (broadcastLabel) return broadcastLabel;

    const parts = [monitor.type || 'Monitor'];
    if (monitor.interval) parts.push(monitor.interval);
    if (monitor.indexes) {
        const firstIndex = String(monitor.indexes).split(',')[0].trim();
        if (firstIndex) parts.push(firstIndex);
    }
    if (monitor.id) parts.push(`#${monitor.id}`);

    return parts.join(' · ');
}

export function formatMonitorOptionLabel(monitor) {
    const int = monitor.interval ? `_${monitor.interval}` : '';
    let cond = '';
    if (monitor.indexes) {
        const mt = monitor.indexes.match(/([A-Z_]+)(\(|\s|>|<)/);
        if (mt) cond = `_${mt[1]}`;
    }
    const summary = `${monitor.symbol || '*'}${int}${cond}`.replace(/__+/g, '_');
    const displayName = getMonitorDisplayName(monitor);
    return `${summary} — ${displayName}`;
}

export function buildMonitorIndexOption(monitor) {
    const customName = (monitor?.name || '').trim();
    if (!customName) return null;

    const int = monitor.interval ? `_${monitor.interval}` : '';
    let cond = '';
    if (monitor.indexes) {
        const mt = monitor.indexes.match(/([A-Z_]+)(\(|\s|>|<)/);
        if (mt) cond = `_${mt[1]}`;
    }

    return {
        eval: `MEMORY['${customName}']`,
        name: `${monitor.symbol}${int}${cond}_${customName}`.replace(/__+/g, '_'),
        symbol: monitor.symbol,
        variable: customName,
        monitorId: monitor.id || null,
    };
}

export function getMonitorsForAutomationIndexes({
    setupSymbol,
    existingMonitors,
    monitorData,
    createdMonitorId,
}) {
    const monitors = [...existingMonitors];
    const hasConfiguredMonitor = monitorData?.name && String(monitorData.indexes || '').trim();

    if (!hasConfiguredMonitor) {
        return filterMonitorsBySymbol(monitors, setupSymbol);
    }

    const currentMonitor = {
        ...monitorData,
        symbol: setupSymbol,
        id: createdMonitorId || monitorData.id || null,
    };

    const existingIndex = monitors.findIndex(
        (monitor) => (createdMonitorId && String(monitor.id) === String(createdMonitorId))
            || monitor.name === currentMonitor.name,
    );

    if (existingIndex >= 0) {
        monitors[existingIndex] = { ...monitors[existingIndex], ...currentMonitor };
    } else {
        monitors.push(currentMonitor);
    }

    return filterMonitorsBySymbol(monitors, setupSymbol);
}

function removeRepeatedAndSort(indexList) {
    return indexList
        .filter((item, index, self) => index === self.findIndex((t) => t.eval === item.eval))
        .sort((a, b) => (a.variable > b.variable ? 1 : -1));
}

function isFuturesOnlyIndex(index) {
    const variable = index?.variable || '';
    const evalText = index?.eval || '';
    return /FLAST_ORDER_|FWALLET_|POSITION_|LAST_LIQ|MARK_PRICE/.test(variable)
        || /FLAST_ORDER_|FWALLET_|POSITION_|LAST_LIQ|MARK_PRICE/.test(evalText);
}

function isSpotOnlyIndex(index) {
    const variable = index?.variable || '';
    const evalText = index?.eval || '';

    if (/FWALLET_|FLAST_ORDER_|POSITION_|LAST_LIQ|MARK_PRICE/.test(variable)
        || /FWALLET_|FLAST_ORDER_|POSITION_|LAST_LIQ|MARK_PRICE/.test(evalText)) {
        return false;
    }

    return (/WALLET_/.test(variable) && !/FWALLET_/.test(variable))
        || (/LAST_ORDER_/.test(variable) && !/FLAST_ORDER_/.test(variable))
        || (/WALLET_/.test(evalText) && !/FWALLET_/.test(evalText))
        || (/LAST_ORDER_/.test(evalText) && !/FLAST_ORDER_/.test(evalText));
}

export function filterIndexesForMarket(indexes, market) {
    const normalized = String(market || 'SPOT').toUpperCase();

    return (indexes || []).filter((index) => {
        if (normalized === 'FUTURES') return !isSpotOnlyIndex(index);
        if (normalized === 'SPOT') return !isFuturesOnlyIndex(index);
        return true;
    });
}

function getWalletIndexes(indexList, symbolObj, market = 'SPOT') {
    if (!symbolObj?.base) return [];

    const walletIndexes = [];
    const normalizedMarket = String(market || 'SPOT').toUpperCase();

    if (normalizedMarket === 'SPOT') {
        const baseWallet = indexList.find((ix) => ix.variable.startsWith('WALLET_') && symbolObj.base === ix.symbol);
        if (baseWallet) walletIndexes.push(baseWallet);

        const quoteWallet = indexList.find(
            (ix) => ix.variable.startsWith('WALLET_') && symbolObj.quote === ix.symbol.replace('*', ''),
        );
        if (quoteWallet) walletIndexes.push(quoteWallet);
    }

    if (normalizedMarket === 'FUTURES') {
        const baseFWallet = indexList.find((ix) => ix.variable.startsWith('FWALLET_') && symbolObj.base === ix.symbol);
        if (baseFWallet) walletIndexes.push(baseFWallet);

        const quoteFWallet = indexList.find(
            (ix) => ix.variable.startsWith('FWALLET_') && symbolObj.quote === ix.symbol.replace('*', ''),
        );
        if (quoteFWallet) walletIndexes.push(quoteFWallet);
    }

    return walletIndexes;
}

export async function buildAutomationIndexesForSymbol({
    setupSymbol,
    symbolMeta = {},
    monitorData,
    createdMonitorId,
    symbolMonitors,
    market = 'SPOT',
}) {
    const automationSymbol = (setupSymbol || '').trim().toUpperCase();
    if (!automationSymbol) return [];

    const [indexList, monitorsFromApi] = await Promise.all([
        getIndexes(),
        Array.isArray(symbolMonitors)
            ? Promise.resolve(symbolMonitors)
            : getMonitorsBySymbol(automationSymbol).catch(() => []),
    ]);

    const monitors = getMonitorsForAutomationIndexes({
        setupSymbol: automationSymbol,
        existingMonitors: monitorsFromApi,
        monitorData,
        createdMonitorId,
    });

    const isWildcard = symbolMeta.base === '*';
    let filteredIndexes = isWildcard
        ? indexList.filter((k) => k.symbol.endsWith(symbolMeta.quote))
        : indexList.filter((k) => k.symbol === automationSymbol || k.symbol === '*');

    if (isWildcard) {
        filteredIndexes.forEach((ix) => {
            if (/^(F?WALLET_\d+)$/.test(ix.variable)) {
                ix.symbol = ix.symbol.replace('*', '');
                ix.eval = ix.eval.replace('*', '');
            } else {
                ix.eval = ix.eval.replace(ix.symbol, automationSymbol);
                ix.symbol = automationSymbol;
            }
        });
    }

    filteredIndexes.push(...monitors.map(buildMonitorIndexOption).filter(Boolean));

    const normalizedMarket = String(market || 'SPOT').toUpperCase();
    if (normalizedMarket === 'SPOT') {
        filteredIndexes.push(...getLastOrderIndexes(automationSymbol));
    } else if (normalizedMarket === 'FUTURES') {
        filteredIndexes.push(...getFuturesLastOrderIndexes(automationSymbol));
        filteredIndexes.push(...getFuturesLiquidationIndexes(automationSymbol));
    }

    filteredIndexes = removeRepeatedAndSort(filteredIndexes);

    if (!isWildcard && symbolMeta?.base) {
        const walletIndexes = getWalletIndexes(indexList, symbolMeta, normalizedMarket);
        filteredIndexes.splice(0, 0, ...walletIndexes);
    }

    filteredIndexes = filterIndexesForMarket(filteredIndexes, normalizedMarket);

    if (filteredIndexes.length === 0) {
        filteredIndexes = filterIndexesForMarket(
            getDefaultIndexes(automationSymbol),
            normalizedMarket,
        );
    }

    return filteredIndexes;
}